import { useQuery } from "@tanstack/react-query";

import { listWorkspaces } from "@/features/workspaces/api";
import { useAuth } from "@/state/AuthContext";
import { isPlatformAdmin } from "@/utils/access";

/**
 * FR-2026-11-04-2 (c): a member who belongs to no workspace gets one line in the Inbox, their Home.
 * It is keyed on the member's own workspace list (the server returns only workspaces they belong
 * to), not the organization's workspace count, which is non-zero whenever anyone has created one.
 * An admin sees every workspace, so the line is never theirs. While the list is loading, or if it
 * fails to load, nothing is claimed.
 */
export default function NoWorkspaceNote(): JSX.Element | null {
  const { user } = useAuth();
  const admin = isPlatformAdmin(user);
  const query = useQuery({
    queryKey: ["workspaces", "mine", user?.organizationId ?? null, user?.id ?? null],
    queryFn: listWorkspaces,
    enabled: !!user && !admin,
  });

  if (!user || admin || !query.isSuccess || (query.data ?? []).length > 0) return null;

  return (
    <div
      className="shrink-0 border-b border-slate-200/80 bg-slate-50 px-4 py-3 text-sm text-slate-700 sm:px-5"
      data-testid="inbox-no-workspace"
      role="status"
    >
      You haven&apos;t been added to a workspace yet. Ask your organization admin.
    </div>
  );
}
